import { tools } from '@worktango/ai-assistant/react';
import { getBooleanFlagValue } from '@worktango/feature-flags-react';
import { coalesce } from '~/helpers/coalesce';
import { fetchCurrentAccountQuery } from '~/behaviors/react-query/account';
import {
  fetchAttributeResponsesQuery,
  fetchOverallSurveyResultsQuery,
  fetchBaseReportQuery,
} from '~/behaviors/react-query/reporting';
import {
  fetchSurveyElementsBySurveysQuery,
  fetchSurveyWithSurveyElements,
} from '~/behaviors/react-query/surveyElements';
import { reportingQueries } from '~/behaviors/queries/reporting.queries';
import { fetchEmployeeAttributesQuery } from '~/behaviors/react-query/attributes';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import { FetchSurveyWithSurveyElementsDocument, FetchSurveyTypesWithCategoriesDocument } from '~/gql/graphql';
import { resolvePeopleLeaderFilter } from './helpers/resolvePeopleLeaderFilter';
import { SurveyAudienceMode } from '~/interfaces/survey.interface';

export const createSurveyAggregatedResultsTool = ({
  peopleLeaderAttributeId,
}: {
  /**
   * This is set in apps/pulse/client/src/views/UI.shared/WorkTangoCoach/WorkTangoCoachPopOut.tsx and will be
   * always truthy for any company that has a people leader attribute.
   */
  peopleLeaderAttributeId: number | null;
}) =>
  tools.createTool({
    name: 'get_survey_aggregated_results',
    description: `Get comprehensive aggregated result data for a survey in a single request. Returns overall scores, factor scores, question scores with metadata, distributions, and breakdowns by attributes. For scale/rating questions this includes the full rating distribution over the question's own scale, with the exact response count and share for every rating; for multiple-choice questions it includes the per-option answer distribution (each option's percentage and response count).

IMPORTANT: When the user wants to filter results FOR a specific people leader/manager (i.e., attributeFilters includes the peopleLeaderAttributeId), check the user's message to see if they mention either:
- full hierarchy - all subordinates at all levels (If selected, set isHierarchyReport: true)
- direct reports only - (If selected, set isHierarchyReport: false)

If they did not mention either, you MUST ask them to choose full hierarchy or direct reports only so that you can determine how to set the isHierarchyReport parameter.

Filtering behavior:
- attributeFilters: Filters the aggregate data to only include the specified attribute values (e.g., Department="Engineering")
- timeframe: ONLY continuous surveys (which collect responses on a rolling basis) can be filtered by a date range; it restricts the scores and distributions to responses submitted within that range. Standard (non-continuous) surveys run for a single fixed period and are always reported over that whole period. Set timeframe whenever the user asks to narrow the period, and omit it otherwise — you do not need to establish whether the survey is continuous first, and you must not ask the user: this tool already knows and returns an error telling you to retry without a timeframe if the survey turns out to be standard.

RESPONSE RATE DATA: By default this tool returns the RESPONSE RATE SPREADS section, which already
identifies which attributes have the widest gaps between their highest- and lowest-responding values.
The exhaustive per-value response rate breakdown is omitted by default to keep the response compact.
Only set includeResponseRatesFor (a list of attribute IDs) when the user explicitly needs the exact
per-value response counts/rates for specific attributes.

NOTE: This tool returns QUANTITATIVE data only. For qualitative/comment analysis,
use the 'survey_comment_analysis' tool with the same surveyId and attributeFilters.
`,
    displayName: 'Analyzing survey results',
    pastTenseDisplayName: 'Analyzed survey results',
    parameters: (t) =>
      t.Object({
        surveyId: t.Number({
          description: 'The numeric ID of the survey to get aggregated data for',
        }),
        isHierarchyReport: t.Optional(
          t.Boolean({
            description:
              'REQUIRED when filtering by people leader/manager: Whether to include the full management hierarchy (true = all subordinates at all levels) or only direct reports (false = immediate reports only). Do NOT set this parameter when filtering by other attributes.',
          }),
        ),
        attributeFilters: t.Optional(
          t.Array(
            t.Object({
              id: t.Number({ description: 'Attribute ID to filter by' }),
              values: t.Array(t.String({ description: 'Attribute values to include' })),
            }),
            {
              description:
                'Optional filters to constrain the aggregate data analysis to specific attribute values (e.g., only data from Engineering department). If not supplied, overall aggregate data is returned, along with full attribute breakdowns by factor and question.',
            },
          ),
        ),
        includeResponseRatesFor: t.Optional(
          t.Array(t.Number({ description: 'Attribute ID to include the per-value response rate breakdown for' }), {
            description:
              "Optional opt-in: attribute IDs for which to include the exhaustive per-attribute-value response rate breakdown (each value's response count and rate). Omitted by default because the RESPONSE RATE SPREADS section already surfaces where response-rate gaps exist. Only set this when the user explicitly needs exact per-value response rates for specific attributes.",
          }),
        ),
        timeframe: t.Optional(
          t.Object(
            {
              startTimeUTC: t.String({
                description:
                  'Start of the range, inclusive, as an ISO 8601 UTC timestamp (e.g. "2026-01-01T00:00:00.000Z").',
              }),
              endTimeUTC: t.String({
                description:
                  'End of the range, inclusive, as an ISO 8601 UTC timestamp (e.g. "2026-03-31T23:59:59.999Z").',
              }),
            },
            {
              description:
                'Optional time range, applicable ONLY to continuous surveys, that restricts the scores and distributions to responses submitted within it. Omit it whenever the user has not asked to narrow the period. Never ask the user whether the survey is continuous — the tool resolves that itself from the survey id. When the user asks for a relative range (e.g. "last 30 days", "this quarter"), compute the concrete start/end from the current date in your context.',
            },
          ),
        ),
      }),
    fulfill: async ({ surveyId, attributeFilters, isHierarchyReport, includeResponseRatesFor, timeframe }) => {
      if (!surveyId) {
        return 'Error: Invalid survey ID. Please provide a valid survey ID.';
      }

      // Check if manager/leader data is being used in any way
      /* istanbul ignore next -- defensive optional chaining for manager filter check */
      const isUsingManagerData = attributeFilters?.some((filter) => filter.id === peopleLeaderAttributeId);

      // Validate that when manager data is used, hierarchy choice is explicit
      /* istanbul ignore next -- manager hierarchy validation */
      if (isUsingManagerData && (isHierarchyReport === undefined || typeof isHierarchyReport !== 'boolean')) {
        return `Error: Manager hierarchy data detected in filters. You MUST ask the user to specify:
- "Do you want direct reports only?" (set isHierarchyReport: false)
- "Do you want full management hierarchy?" (set isHierarchyReport: true)
Wait for their explicit choice before proceeding.`;
      }
      try {
        const [{ account, company }, surveyBasicDataArray, surveyFullDetails] = await Promise.all([
          fetchCurrentAccountQuery(),
          reportingQueries.findSurveys([surveyId]),
          graphqlRequest<any>(FetchSurveyWithSurveyElementsDocument, {
            id: surveyId,
          }),
        ]);

        // First fetch survey basic data to get survey type ID
        const surveyBasicData = surveyBasicDataArray[0];

        // Timeframe filtering is only valid for continuous surveys; standard surveys run for a
        // single fixed period and cannot be sliced by date.
        if (timeframe && !surveyBasicData?.isContinuous) {
          return `Error: A timeframe was provided, but survey ${surveyId} is not a continuous survey. Only continuous surveys can be filtered by a date range. Retry without a timeframe to analyze the full survey period.`;
        }
        /* istanbul ignore next -- defensive optional chaining for surveyTypeId */
        const surveyTypeId = surveyBasicData?.types?.[0]?.id;

        // Get language IDs from the survey data
        const languageIds = extractSurveyLanguageIds(surveyBasicData, account.langId);

        // First fetch survey attributes and survey types which we need for other calls
        const [surveyAttributes, surveyTypesData] = await Promise.all([
          /* istanbul ignore next -- conditional attribute fetching */
          surveyTypeId
            ? fetchEmployeeAttributesQuery({
                companyId: company.id,
                surveyTypeId: surveyTypeId,
                includePrivateAttributes: false,
              })
            : Promise.resolve([]),
          graphqlRequest<any>(FetchSurveyTypesWithCategoriesDocument, {
            companyId: company.id,
            /* istanbul ignore start -- defensive fallback for empty languageIds (extractSurveyLanguageIds always returns a non-empty list) */
            languageIds: languageIds.length > 0 ? languageIds : [1],
            /* istanbul ignore stop */
            includeBase: true,
          }),
        ]);

        // Transform attribute filters to include type information
        const typeMapping: Record<string, string> = {
          string: 'STRING',
          number: 'NUMBER',
          boolean: 'BOOLEAN',
          date: 'DATE',
          email: 'EMAIL',
        };

        const peopleLeaderFilter = await resolvePeopleLeaderFilter({
          companyId: company.id,
          languageId: coalesce(languageIds[0], 1),
          surveyIds: [surveyId],
          peopleLeaderAttributeId,
          attributeFilters,
          isHierarchyReport,
        });

        // An unresolved name must stop the request. Passing the raw name through as an employee id
        // yields zero permitted snapshots, which is indistinguishable from a genuinely empty result
        // and gets reported to the user as an anonymity threshold.
        if (peopleLeaderFilter.error) {
          return peopleLeaderFilter.error;
        }

        /* istanbul ignore start -- complex people-leader label/id mapping and filtering, exercised via the manager-filter tests' output assertions */
        const managerReportIds = peopleLeaderFilter.managerReportIds;

        // Filters with the people leader removed; the backend derives its reports from
        // managerReportIds, so keeping the filter would narrow a hierarchy report to direct reports.
        const otherAttributeFilters = peopleLeaderFilter.attributeFilters;

        const attributeFiltersWithType = otherAttributeFilters?.map((filter) => {
          const attribute = surveyAttributes.find((attr) => attr.id === filter.id);
          if (!attribute) {
            // Default to STRING type if attribute not found
            return {
              ...filter,
              type: 'STRING' as const,
            };
          }

          const typeName = attribute.type.name.toLowerCase();
          const mappedType = coalesce(typeMapping[typeName], 'STRING');

          return {
            ...filter,
            type: mappedType,
          };
        });

        const hasManagerFilter = managerReportIds.length > 0;

        // Fetch all other data in parallel
        const [surveyElements, surveyWithDetails, baseReportData, attributeResponseStats, overallResults] =
          await Promise.all([
            fetchSurveyElementsBySurveysQuery([surveyId]),
            fetchSurveyWithSurveyElements(surveyId),
            // base-report has no managerReportIds parameter; it splits the people leader out of
            // selectedAttributes itself, so it needs the filter kept in place with id values.
            fetchBaseReportQuery({
              surveyIds: [surveyId],
              basedOnHierarchy: Boolean(isHierarchyReport && hasManagerFilter),
              selectedTimeframe: timeframe ?? null,
              ...(peopleLeaderFilter.attributeFiltersWithLeaderIds.length > 0 && {
                selectedAttributes: peopleLeaderFilter.attributeFiltersWithLeaderIds,
              }),
            }),
            fetchAttributeResponsesQuery({
              companyIds: [company.id],
              languageId: account.langId,
              surveyIds: [surveyId],
              isHierarchyReport: Boolean(isHierarchyReport && hasManagerFilter),
              requestManagerHierarchyData: false,
              selectedTimeframe: timeframe ?? null,
              includeResponseRates: surveyBasicData?.audienceMode !== SurveyAudienceMode.Shared,
              ...(hasManagerFilter && { managerReportIds }),
              ...(attributeFiltersWithType &&
                attributeFiltersWithType.length > 0 && { attributes: attributeFiltersWithType }),
            }),
            fetchOverallSurveyResultsQuery({
              surveyIds: [surveyId],
              languageId: account.langId,
              companyIds: [company.id],
              isHierarchyReport: Boolean(isHierarchyReport && hasManagerFilter),
              selectedTimeframe: timeframe ?? null,
              ...(hasManagerFilter && { managerReportIds }),
              ...(attributeFiltersWithType &&
                attributeFiltersWithType.length > 0 && { attributes: attributeFiltersWithType }),
            }),
          ]);
        /* istanbul ignore stop */

        /* istanbul ignore next -- anonymity threshold check */
        if (overallResults.length === 0) {
          //No data to fetch, which indicates that the selected filter did not match the anonymity threshold!
          return `There was not enough data to generate results based on your selection. This is likely due to anonymity thresholds set by your organization. Please try adjusting your filters or contact your administrator for more information.`;
        }

        // Process and aggregate all data
        const aggregatedData = processAggregatedData({
          account,
          company,
          surveyBasicData,
          surveyWithDetails,
          attributeResponseStats,
          surveyElements,
          baseReportData,
          overallResults,
          surveyFullDetails: surveyFullDetails.data,
          surveyAttributes,
          surveyTypesData: surveyTypesData.data,
        });

        // The `coach-receives-response-rates` flag preserves the legacy behaviour of always emitting
        // the full per-attribute-value response rate breakdown, and defaults to on so behaviour is
        // unchanged until the flag is explicitly turned off. When it is off, the breakdown is dropped
        // from the default output and only returned for attributes the caller explicitly opts into
        // via includeResponseRatesFor.
        const includeAllResponseRates = getBooleanFlagValue('coach-receives-response-rates', true);

        // Convert to LLM-friendly narrative format
        return formatAggregatedDataAsNarrative(aggregatedData, includeResponseRatesFor, includeAllResponseRates);
      } catch (error) {
        return 'Failed to fetch survey aggregated data. Please try again later.';
      }
    },
  });

interface AttributeSpread {
  attribute: string;
  spread: number;
  minValue: string;
  minScore: number;
  maxValue: string;
  maxScore: number;
  valueCount: number;
}

interface ResponseRateSpread {
  attribute: string;
  attributeId: number;
  spread: number;
  minValue: string;
  minResponseRate: number;
  maxValue: string;
  maxResponseRate: number;
  valueCount: number;
}

interface ResponseRateByValue {
  attribute: string;
  attributeId: number;
  value: string;
  responseCount: number;
  totalCount: number;
  responseRate: number;
}

interface EnpsResult {
  score: number;
  promoters: number;
  passives: number;
  detractors: number;
}

interface AggregatedSurveyData {
  surveyId: number;
  surveyName: string;
  surveyType: string;
  respondents: number;
  /** Null for an Open Survey, which has no invited population. */
  responseRate: number | null;
  startDate: string;
  endDate: string;
  responseRateSpreads: Array<ResponseRateSpread>;
  responseRatesByAttributeValue: Array<ResponseRateByValue>;
  factors: Array<{
    id: number;
    name: string;
    scores: {
      favorable: number;
      neutral: number;
      unfavorable: number;
      average: number;
    };
    isEnps: boolean;
    enpsScores: EnpsResult | null;
    variability?: {
      mean: number;
      standardDeviation: number;
      variance: number;
      coefficientOfVariation: number;
      min: number;
      max: number;
      range: number;
      sampleSize: number;
    };
    attributeSpreads: Array<AttributeSpread>;
    attributeBreakdown: Array<{
      attribute: string;
      value: string;
      scores: {
        favorable: number;
        neutral: number;
        unfavorable: number;
        average: number;
        responseCount: number;
      };
    }>;
  }>;
  questions: Array<{
    id: number;
    title: string;
    factorId: number;
    factorName: string;
    type: string;
    orderIndex: number;
    isRequired: boolean;
    allowComments: boolean;
    translations?: Record<
      string,
      {
        languageId: number;
        title: string;
        desc: string;
        minLabel?: string;
        maxLabel?: string;
      }
    >;
    scores: {
      favorable: number;
      neutral: number;
      unfavorable: number;
      average: number;
    };
    answeredCount?: number;
    respondentCount?: number;
    ratingScale?: {
      min: number;
      max: number;
    };
    ratingDistribution?: Array<{
      rating: number;
      count: number;
      percentage: number;
    }>;
    enps?: EnpsResult;
    optionDistribution?: Array<{
      label: string;
      percentage: number;
      count: number;
    }>;
    variability?: {
      mean: number;
      standardDeviation: number;
      variance: number;
      coefficientOfVariation: number;
      min: number;
      max: number;
      range: number;
      sampleSize: number;
    };
    attributeSpreads: Array<AttributeSpread>;
    attributeBreakdown: Array<{
      attribute: string;
      value: string;
      scores: {
        favorable: number;
        neutral: number;
        unfavorable: number;
        average: number;
        responseCount: number;
      };
    }>;
  }>;
  attributes: Array<{
    id: number;
    name: string;
    values: string[];
  }>;
}

interface ProcessAggregatedDataParams {
  account: {
    userId: number;
    employeeId: null;
    userRoleId: null;
    hierarchy: any[];
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    lang: string;
    langId: number;
    avatarUrl: null;
    isAdmin: boolean;
    updatedAt: string;
    createdAt: string;
    companyId: number;
    companyLogoUrl: null;
    companyColor: null;
    companyName: string;
    supportedLanguages: string[];
    workspace: null;
    companyFeatureSettings: {
      COMMENT_SENTIMENT_AND_SUMMARY: boolean;
    };
  };
  company: {
    id: number;
    name: string;
    workspace: null;
    lang: string;
    languageId?: number;
    timezones: {
      id: number;
      name: string;
    }[];
    color: null;
    salesforceId: null;
    logoUrl: null;
    languages: number[];
    supportedLanguages: string[];
    kioskAttributeId?: number;
    createdAt?: string;
    updatedAt?: string;
    companyFeatureSettings: {
      COMMENT_SENTIMENT_AND_SUMMARY: boolean;
    };
  };
  surveyBasicData: {
    id: number;
    anonymousConversation: boolean;
    namesPerLanguage: {
      id?: number;
      surveyName: string;
      language: {
        langISOCode: string;
        id?: number;
      };
      emailSubject?: null;
      emailBody?: null;
    }[];
    types: {
      id: number;
      threshold: number;
    }[];
    startTimeUTC: string;
    isContinuous: boolean;
    audienceMode?: SurveyAudienceMode;
  };
  surveyWithDetails: {
    id: number;
    name: string;
    surveyTypeId: number;
    surveyElements: {
      id: number;
      questionId: number;
      orderIndex: number;
      companyId: number;
      typeId: number;
      typeName: string;
      typeKey: string;
      categoryId: number;
      isRequired: boolean;
      allowComments?: boolean;
      showSkipButton: boolean;
      showLabels?: boolean;
      showDescription: boolean;
      hideReporting: boolean;
      isLibraryQuestion: boolean;
      isPreviousQuestion: boolean;
      isBenchmarkedQuestion?: boolean;
      isAttributeBased: boolean;
      isDiscontinued?: boolean;
      discontinuedDateUTC?: null;
      selectedAttributes: Record<string, any>;
      minOptions?: number;
      maxOptions?: number;
      title: string;
      translations: {
        [key: string]: {
          languageId: number;
          title: string;
          desc: string | null;
          minLabel: string;
          maxLabel: string;
        };
      };
      iconId?: number;
      iconName?: string;
      iconUrl: string | null;
      allowMultipleSelect?: boolean;
      supportedLanguages: string;
      lastSent: null;
    }[];
  };
  attributeResponseStats: {
    surveys: {
      id: number;
      name: string;
      percentage: number;
      count: number;
      totalCount: number;
    }[];
    attributes: {
      id: number;
      name: string;
      spread: {
        min: number;
        max: number;
      };
      count: number;
      values: {
        id: string;
        label: string;
        count: number;
        totalCount: number;
        percentage: number;
      }[];
    }[];
  };
  surveyElements: {
    id: number;
    name: string;
    surveyTypeId: number;
    surveyElements: {
      id: number;
      questionId: number;
      orderIndex: number;
      companyId: number;
      typeId: number;
      typeName: string;
      typeKey: string;
      categoryId: number;
      isRequired: boolean;
      allowComments?: boolean;
      showSkipButton: boolean;
      showLabels?: boolean;
      showDescription: boolean;
      hideReporting: boolean;
      isLibraryQuestion: boolean;
      isPreviousQuestion: boolean;
      isBenchmarkedQuestion?: boolean;
      isAttributeBased: boolean;
      isDiscontinued?: boolean;
      discontinuedDateUTC?: null;
      selectedAttributes: Record<string, any>;
      minOptions?: number;
      maxOptions?: number;
      title: string;
      translations: {
        [key: string]: {
          languageId: number;
          title: string;
          desc: string | null;
          minLabel: string;
          maxLabel: string;
        };
      };
      iconId?: number;
      iconName?: string;
      iconUrl: string | null;
      allowMultipleSelect?: boolean;
      supportedLanguages: string;
      lastSent: null;
    }[];
  }[];
  baseReportData: {
    overallResult: {
      responseRate: {
        respondentCount: number;
        recipientCount: number;
      };
      attributeResults: {
        id: string;
        employeeAttribute: number;
        isHierarchyAttribute: boolean;
        attributeValueResults: {
          id: string;
          isAnonymized: boolean;
          attributeValue: string;
          attributeValueLabel: string;
          scaleDistribution: {
            favorablePercent: number;
            neutralPercent: number;
            unfavorablePercent: number;
          } | null;
          enpsDistribution: null;
          scoreAverage: number | null;
          enpsScore: null;
          responseRate?: {
            respondentCount: number;
            recipientCount: number;
          };
          scaleRespondentCount: number;
          respondentPercent: number | null;
          insight?: null;
        }[];
        maxRespondentPercent: number;
        minRespondentPercent: number;
        maxScaleFavorable: number | null;
        minScaleFavorable: number | null;
        maxEnpsFavorable: number | null;
        minEnpsFavorable: number | null;
        maxScoreAverage: number | null;
        minScoreAverage: number | null;
        maxEnpsScore: number | null;
        minEnpsScore: number | null;
      }[];
      insightMeta: {
        lastMeasured: string;
        differCompareToLastMeasured: null;
        containLastMeasuredDiffer: boolean;
        containOrgWideDiffer: boolean;
        containOrgWideRank: boolean;
      };
      id: string;
    };
    categoryResults: {
      id: number;
      category: number;
      isAnonymized: boolean;
      isKey: boolean;
      scaleDistribution: {
        favorablePercent: number;
        neutralPercent: number;
        unfavorablePercent: number;
      };
      scoreAverage: number;
      enpsDistribution: {
        favorablePercent: number;
        neutralPercent: number;
        unfavorablePercent: number;
      } | null;
      enpsScore: number | null;
      insight: Record<string, any>;
      surveyElementResults: {
        id: string;
        surveyElement: number;
        isAnonymized: boolean;
        isKey: boolean;
        scaleDistribution: {
          favorablePercent: number;
          neutralPercent: number;
          unfavorablePercent: number;
        } | null;
        scoreAverage: number | null;
        // Populated for eNPS questions, which in turn have no scaleDistribution.
        enpsDistribution: {
          favorablePercent: number;
          neutralPercent: number;
          unfavorablePercent: number;
        } | null;
        enpsScore: number | null;
        insight: Record<string, any>;
        attributeResults: {
          id: string;
          employeeAttribute: number;
          isHierarchyAttribute: boolean;
          attributeValueResults: {
            id: string;
            insight: null;
            isAnonymized: boolean;
            attributeValue: string;
            attributeValueLabel: string;
            scaleDistribution: {
              favorablePercent: number;
              neutralPercent: number;
              unfavorablePercent: number;
            } | null;
            enpsDistribution: null;
            scoreAverage: number | null;
            enpsScore: null;
            scaleRespondentCount: number;
            respondentPercent: number | null;
          }[];
          maxRespondentPercent: number;
          minRespondentPercent: number;
          maxScaleFavorable: number | null;
          minScaleFavorable: number | null;
          maxEnpsFavorable: number | null;
          minEnpsFavorable: number | null;
          maxScoreAverage: number | null;
          minScoreAverage: number | null;
          maxEnpsScore: null;
          minEnpsScore: null;
        }[];
      }[];
      attributeResults: {
        id: string;
        employeeAttribute: number;
        isHierarchyAttribute: boolean;
        attributeValueResults: {
          id: string;
          insight: null;
          isAnonymized: boolean;
          attributeValue: string;
          attributeValueLabel: string;
          scaleDistribution: {
            favorablePercent: number;
            neutralPercent: number;
            unfavorablePercent: number;
          } | null;
          enpsDistribution: null;
          scoreAverage: number | null;
          enpsScore: null;
          scaleRespondentCount: number;
          respondentPercent: number | null;
        }[];
        maxRespondentPercent: number;
        minRespondentPercent: number;
        maxScaleFavorable: number | null;
        minScaleFavorable: number | null;
        maxEnpsFavorable: number | null;
        minEnpsFavorable: number | null;
        maxScoreAverage: number | null;
        minScoreAverage: number | null;
        maxEnpsScore: null;
        minEnpsScore: null;
      }[];
      taskTemplates: any[];
      learningContents: any[];
    }[];
    learningContents: any[];
    taskTemplates: any[];
  };
  overallResults: {
    id: number;
    surveyElementId: number;
    type: string;
    text: string;
    totalAnswered: number;
    totalRespondents: number;
    numberOfComments: number;
    iconPath: string | null;
    minRating: number | null;
    maxRating: number | null;
    average: number;
    addedDateUTC: null;
    discontinuedDateUTC: null;
    ratings:
      | {
          rating: number;
          totalAnswered: number;
          numberOfComments: number;
          percentage: number;
        }[]
      | null;
    options:
      | {
          id: number | string;
          text: string;
          totalAnswered: number;
          numberOfComments: number;
          percentage: number;
        }[]
      | null;
    aggregatedCommentSentiments: {
      numberOfPositiveComments: number;
      numberOfNeutralComments: number;
      numberOfNegativeComments: number;
      numberOfUnknownComments: number;
    } | null;
  }[];
  surveyFullDetails: {
    survey: {
      id: number;
      hashCode: string;
      showResults: boolean;
      status: {
        id: number;
        name: string;
      };
      anonymousConversation: boolean;
      namesPerLanguage: {
        language: {
          id: number;
          langISOCode: string;
        };
        surveyName: string;
        emailSubject: null;
        emailBody: null;
      }[];
      sendingEmailAddress: {
        id: number;
      };
      timezone: {
        id: number;
        timezone: string;
      };
      employees: {
        id: number;
        surveyInviteDate: string;
      }[];
      fromEmailName: null;
      startTimeUTC: string;
      endTimeUTC: string;
      combineLanguages: boolean;
      types: {
        id: number;
        threshold: number;
      }[];
      questions: {
        id: number;
        companyId: number;
        orderIndex: number;
        isLibraryQuestion: boolean;
        isPreviousQuestion: boolean;
        isBenchmarkedQuestion: boolean;
        type: {
          name: string;
        };
        showSkipButton: boolean;
        isAttributeBased: boolean;
        hideReporting: boolean;
        attributes: any[];
        discontinuedDateUTC: null;
        addedDateUTC: string;
        isDiscontinued: boolean;
        question: {
          id: number;
          messagesPerLanguage: {
            language: {
              id: number;
              langISOCode: string;
            };
            message: string;
            description: string | null;
            labelBefore?: string;
            labelAfter?: string;
          }[];
          category: {
            id: number;
            categoryNames?: {
              name: string;
            }[];
          };
          icon?: {
            id: number;
            name: string;
          };
          ratingOptionLowest?: number;
          ratingOptionHighest?: number;
          required: boolean;
          showDescription: boolean;
          showTextComment?: boolean;
          showLabels?: boolean;
        };
      }[];
      disableEmailSending: boolean;
      isContinuous: boolean;
      invitationExpiryIn: null;
      lifecycleSurveyTypeId: null;
      companyEmployeeIdsInSurvey: any[];
    };
  };
  surveyAttributes: {
    id: number;
    baseAttributeId: null;
    companyId: number;
    type: {
      id: number;
      name: string;
    };
    attributeNames: {
      id?: number;
      name: string;
    }[];
  }[];
  surveyTypesData: {
    companySurveyTypes: {
      id: number;
      threshold: number;
      names: {
        name: string;
      }[];
      categories: {
        categoryId: number;
        names: {
          name: string;
        }[];
        isKeyFactor: boolean;
        isBaseCategory: boolean;
      }[];
    }[];
  };
}

// Helper function to format timestamps to readable dates
export function formatDate(timestamp: string | number): string {
  if (!timestamp) return 'Not specified';

  // Handle both string and number timestamps
  const ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;

  // Check if it's a valid timestamp
  if (isNaN(ts)) return 'Not specified';

  const date = new Date(ts);

  // Check if date is valid
  /* istanbul ignore start -- unreachable: a numeric timestamp always yields a valid Date */
  if (isNaN(date.getTime())) return 'Not specified';
  /* istanbul ignore stop */

  // Format as "MMM DD, YYYY"
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  return date.toLocaleDateString('en-US', options);
}

function toOneDecimal(value: number): number {
  return Number(value.toFixed(1));
}

/**
 * Extracts language IDs from survey data, falling back to account language
 */
export function extractSurveyLanguageIds(surveyBasicData: any, accountLangId: number): number[] {
  const ids = surveyBasicData?.namesPerLanguage
    ?.map((lang: { language: { id: number } }) => lang.language?.id)
    .filter(Boolean);
  return ids && ids.length > 0 ? ids : [accountLangId];
}

/* istanbul ignore start -- pure data-shaping and narrative formatting: branch-heavy defensive optional chaining, exercised through output assertions in tests */

/**
 * Translates the reporting API's eNPS payload into eNPS terms.
 *
 * The API carries eNPS in a structure shaped like the favourability one, so promoters arrive as
 * `favorablePercent`, passives as `neutralPercent` and detractors as `unfavorablePercent`. Renaming
 * them here keeps favourability wording out of the narrative, where it invites the reader to treat
 * an eNPS score as a favourability score. Scores arrive as a -1..1 fraction and are scaled to the
 * conventional -100..100 range.
 *
 * Returns null when the item has no eNPS, which is the case for every question that is not set up
 * as an eNPS question.
 */
function toEnpsResult(
  enpsScore: number | null | undefined,
  enpsDistribution: { favorablePercent: number; neutralPercent: number; unfavorablePercent: number } | null,
): EnpsResult | null {
  if (enpsScore === null || enpsScore === undefined) {
    return null;
  }

  return {
    score: Number((enpsScore * 100).toFixed(1)),
    promoters: Math.round((enpsDistribution?.favorablePercent || 0) * 100),
    passives: Math.round((enpsDistribution?.neutralPercent || 0) * 100),
    detractors: Math.round((enpsDistribution?.unfavorablePercent || 0) * 100),
  };
}

function processAggregatedData({
  surveyBasicData,
  surveyElements,
  surveyWithDetails,
  attributeResponseStats,
  baseReportData,
  overallResults,
  surveyFullDetails,
  surveyAttributes,
  surveyTypesData,
}: ProcessAggregatedDataParams): AggregatedSurveyData {
  // Get survey name
  const surveyName = coalesce(
    surveyBasicData.namesPerLanguage?.[0]?.surveyName || surveyWithDetails.name,
    'Unknown Survey',
  );

  // Get survey type name
  const surveyTypeId = surveyBasicData.types?.[0]?.id;
  const surveyType = coalesce(
    surveyTypesData?.companySurveyTypes?.find((st) => st.id === surveyTypeId)?.names?.[0]?.name,
    'Unknown Type',
  );

  // Extract response data - use attributeResponseStats which respects filters
  const surveyStats = attributeResponseStats?.surveys?.[0];
  const respondents = surveyStats?.count || 0;
  const recipients = surveyStats?.totalCount || 0;
  const isOpenSurvey = surveyBasicData.audienceMode === SurveyAudienceMode.Shared;
  const responseRatePercentage = surveyStats?.percentage ? Math.round(surveyStats.percentage * 100) : 0;

  // Extract dates
  const startDateRaw = surveyBasicData.startTimeUTC || surveyFullDetails?.survey?.startTimeUTC || '';
  const endDateRaw = surveyFullDetails?.survey?.endTimeUTC || '';
  const startDate = formatDate(startDateRaw);
  const endDate = formatDate(endDateRaw);

  // Process response rate spreads and by attribute value
  const responseRateSpreads: ResponseRateSpread[] = [];
  const responseRatesByAttributeValue: ResponseRateByValue[] = [];

  attributeResponseStats?.attributes?.forEach((attr) => {
    const validValues = attr.values.filter((v) => v.percentage !== undefined && v.percentage !== null);
    if (validValues.length > 0) {
      const percentages = validValues.map((v) => v.percentage);
      const minResponseRate = Math.min(...percentages);
      const maxResponseRate = Math.max(...percentages);
      const spread = maxResponseRate - minResponseRate;

      const minValue = validValues.find((v) => v.percentage === minResponseRate);
      const maxValue = validValues.find((v) => v.percentage === maxResponseRate);

      if (spread > 0) {
        responseRateSpreads.push({
          attribute: attr.name,
          attributeId: attr.id,
          spread: Math.round(spread),
          minValue: minValue?.label || '',
          minResponseRate: Math.round(minResponseRate),
          maxValue: maxValue?.label || '',
          maxResponseRate: Math.round(maxResponseRate),
          valueCount: validValues.length,
        });
      }

      // Add individual response rates
      validValues.forEach((value) => {
        responseRatesByAttributeValue.push({
          attribute: attr.name,
          attributeId: attr.id,
          value: value.label,
          responseCount: value.count,
          totalCount: value.totalCount,
          responseRate: Math.round(value.percentage * 100),
        });
      });
    }
  });

  // Sort response rates by attribute and then by response rate
  responseRatesByAttributeValue.sort((a, b) => {
    if (a.attribute !== b.attribute) {
      return a.attribute.localeCompare(b.attribute);
    }
    return b.responseRate - a.responseRate;
  });

  // Create category ID to name mapping
  const categoryMap = new Map<number, string>();
  surveyTypesData?.companySurveyTypes?.forEach((st) => {
    st.categories?.forEach((cat) => {
      categoryMap.set(cat.categoryId, cat.names?.[0]?.name || `Category ${cat.categoryId}`);
    });
  });

  // Process categories/factors
  const factors: AggregatedSurveyData['factors'] = [];
  const categoryResults = baseReportData?.categoryResults || [];

  categoryResults.forEach((categoryResult) => {
    const categoryName = categoryMap.get(categoryResult.category) || `Factor ${categoryResult.category}`;
    const factorEnps = toEnpsResult(categoryResult.enpsScore, categoryResult.enpsDistribution);

    const factor = {
      id: categoryResult.category,
      name: categoryName,
      isEnps: factorEnps !== null,
      enpsScores: factorEnps,
      scores: {
        favorable: Math.round((categoryResult.scaleDistribution?.favorablePercent || 0) * 100),
        neutral: Math.round((categoryResult.scaleDistribution?.neutralPercent || 0) * 100),
        unfavorable: Math.round((categoryResult.scaleDistribution?.unfavorablePercent || 0) * 100),
        average: categoryResult.scoreAverage || 0,
      },
      attributeSpreads: [] as AttributeSpread[],
      attributeBreakdown: [] as any[],
    };

    // Process attribute results for this factor
    const attributeBreakdownMap = new Map<string, any[]>();
    const attributeScoresMap = new Map<string, { min: number; max: number; minValue: string; maxValue: string }>();

    categoryResult.attributeResults?.forEach((attrResult) => {
      const attrName =
        surveyAttributes.find((a) => a.id === attrResult.employeeAttribute)?.attributeNames?.[0]?.name ||
        `Attribute ${attrResult.employeeAttribute}`;

      attrResult.attributeValueResults?.forEach((valueResult) => {
        if (valueResult.scaleDistribution && valueResult.scoreAverage !== null) {
          const favorable = Math.round((valueResult.scaleDistribution.favorablePercent || 0) * 100);

          // Track for spreads
          if (!attributeScoresMap.has(attrName)) {
            attributeScoresMap.set(attrName, {
              min: favorable,
              max: favorable,
              minValue: valueResult.attributeValueLabel,
              maxValue: valueResult.attributeValueLabel,
            });
          } else {
            const current = attributeScoresMap.get(attrName)!;
            if (favorable < current.min) {
              current.min = favorable;
              current.minValue = valueResult.attributeValueLabel;
            }
            if (favorable > current.max) {
              current.max = favorable;
              current.maxValue = valueResult.attributeValueLabel;
            }
          }

          // Add to breakdown
          if (!attributeBreakdownMap.has(attrName)) {
            attributeBreakdownMap.set(attrName, []);
          }

          attributeBreakdownMap.get(attrName)!.push({
            attribute: attrName,
            value: valueResult.attributeValueLabel,
            scores: {
              favorable: Math.round((valueResult.scaleDistribution.favorablePercent || 0) * 100),
              neutral: Math.round((valueResult.scaleDistribution.neutralPercent || 0) * 100),
              unfavorable: Math.round((valueResult.scaleDistribution.unfavorablePercent || 0) * 100),
              average: valueResult.scoreAverage || 0,
              responseCount: valueResult.scaleRespondentCount || 0,
            },
          });
        }
      });
    });

    // Calculate spreads
    attributeScoresMap.forEach((scores, attrName) => {
      const spread = scores.max - scores.min;
      if (spread > 0) {
        factor.attributeSpreads.push({
          attribute: attrName,
          spread: Math.round(spread),
          minValue: scores.minValue,
          minScore: scores.min,
          maxValue: scores.maxValue,
          maxScore: scores.max,
          valueCount: attributeBreakdownMap.get(attrName)?.length || 0,
        });
      }
    });

    // Flatten attribute breakdown and sort by favorable score
    attributeBreakdownMap.forEach((breakdowns) => {
      factor.attributeBreakdown.push(...breakdowns);
    });
    factor.attributeBreakdown.sort((a, b) => a.scores.favorable - b.scores.favorable);

    factors.push(factor);
  });

  // Process questions
  const questions: AggregatedSurveyData['questions'] = [];

  // Create a map of survey element results
  const surveyElementResultsMap = new Map<number, any>();
  categoryResults.forEach((catResult) => {
    catResult.surveyElementResults?.forEach((seResult) => {
      surveyElementResultsMap.set(seResult.surveyElement, seResult);
    });
  });

  // Create a map of overall results
  const overallResultsMap = new Map<number, (typeof overallResults)[number]>();
  overallResults?.forEach((result) => {
    overallResultsMap.set(result.surveyElementId, result);
  });

  // Process all survey elements that have results
  surveyElementResultsMap.forEach((elementResult, elementId) => {
    // Get the overall result for this element
    const overallResult = overallResultsMap.get(elementId);

    // An eNPS question is scored as eNPS instead of favourability, so it has no scale distribution.
    // Skip only when the question has neither, which is also how anonymized questions arrive.
    const questionEnps = toEnpsResult(elementResult.enpsScore, elementResult.enpsDistribution);
    if (!elementResult.scaleDistribution && !questionEnps) {
      return;
    }

    // Try to find the element definition from various sources
    const allSurveyElements = surveyElements?.[0]?.surveyElements || surveyWithDetails?.surveyElements || [];
    let element = allSurveyElements.find((e) => e.id === elementId);

    // If not found in survey elements, check surveyFullDetails
    if (!element && surveyFullDetails?.survey?.questions) {
      const fullDetailQuestion = surveyFullDetails.survey.questions.find((q: any) => q.id === elementId);
      if (fullDetailQuestion) {
        element = {
          id: elementId,
          title: fullDetailQuestion.question?.messagesPerLanguage?.[0]?.message || '',
          categoryId: fullDetailQuestion.question?.category?.id,
          typeName: fullDetailQuestion.type?.name || '',
          typeKey: 'scale',
          orderIndex: fullDetailQuestion.orderIndex,
          isRequired: fullDetailQuestion.question?.required || false,
          allowComments: fullDetailQuestion.question?.showTextComment || false,
          translations: {},
        } as any;
      }
    }

    // If still no element found, check overallResults for title
    if (!element && overallResult) {
      element = {
        id: elementId,
        title: overallResult.text || `Question ${elementId}`,
        categoryId:
          categoryResults.find((cr) => cr.surveyElementResults?.some((ser: any) => ser.surveyElement === elementId))
            ?.category || 0,
        typeName: 'Scale',
        typeKey: 'RATING',
        orderIndex: 0,
        isRequired: false,
        allowComments: false,
        translations: {},
      } as any;
    }

    const question: AggregatedSurveyData['questions'][0] = {
      id: elementId,
      title: element?.title || overallResult?.text || '',
      factorId: element?.categoryId || 0,
      factorName: categoryMap.get(element?.categoryId || 0) || `Factor ${element?.categoryId || 0}`,
      type: element?.typeName || element?.typeKey || overallResult?.type || '',
      orderIndex: element?.orderIndex || 0,
      isRequired: element?.isRequired || false,
      allowComments: element?.allowComments || false,
      translations:
        element?.translations && Object.keys(element.translations).length > 0
          ? Object.entries(element.translations).reduce(
              (acc, [key, value]: [string, any]) => {
                acc[key] = {
                  ...value,
                  desc: value.desc || '',
                };
                return acc;
              },
              {} as Record<string, any>,
            )
          : undefined,
      scores: {
        favorable: Math.round((elementResult?.scaleDistribution?.favorablePercent || 0) * 100),
        neutral: Math.round((elementResult?.scaleDistribution?.neutralPercent || 0) * 100),
        unfavorable: Math.round((elementResult?.scaleDistribution?.unfavorablePercent || 0) * 100),
        average: elementResult?.scoreAverage || 0,
      },
      attributeSpreads: [],
      attributeBreakdown: [],
    };

    if (questionEnps) {
      question.enps = questionEnps;
    }

    question.answeredCount = overallResult?.totalAnswered;
    question.respondentCount = overallResult?.totalRespondents;

    if (typeof overallResult?.minRating === 'number' && typeof overallResult?.maxRating === 'number') {
      question.ratingScale = { min: overallResult.minRating, max: overallResult.maxRating };
    }

    // Add distribution if available
    if (overallResult?.ratings && Array.isArray(overallResult.ratings)) {
      question.ratingDistribution = overallResult.ratings.map((rating) => ({
        rating: rating.rating,
        count: coalesce(rating.totalAnswered, 0),
        // The API returns percentage as a 0-1 fraction; scale to a whole percent.
        percentage: toOneDecimal(coalesce(rating.percentage, 0) * 100),
      }));
    }

    // Process attribute results for this question
    const attributeBreakdownMap = new Map<string, any[]>();
    const attributeScoresMap = new Map<string, { min: number; max: number; minValue: string; maxValue: string }>();

    elementResult?.attributeResults?.forEach((attrResult: any) => {
      const attrName =
        surveyAttributes.find((a) => a.id === attrResult.employeeAttribute)?.attributeNames?.[0]?.name ||
        `Attribute ${attrResult.employeeAttribute}`;

      attrResult.attributeValueResults?.forEach((valueResult: any) => {
        if (valueResult.scaleDistribution && valueResult.scoreAverage !== null) {
          const favorable = Math.round((valueResult.scaleDistribution.favorablePercent || 0) * 100);

          // Track for spreads
          if (!attributeScoresMap.has(attrName)) {
            attributeScoresMap.set(attrName, {
              min: favorable,
              max: favorable,
              minValue: valueResult.attributeValueLabel,
              maxValue: valueResult.attributeValueLabel,
            });
          } else {
            const current = attributeScoresMap.get(attrName)!;
            if (favorable < current.min) {
              current.min = favorable;
              current.minValue = valueResult.attributeValueLabel;
            }
            if (favorable > current.max) {
              current.max = favorable;
              current.maxValue = valueResult.attributeValueLabel;
            }
          }

          // Add to breakdown
          if (!attributeBreakdownMap.has(attrName)) {
            attributeBreakdownMap.set(attrName, []);
          }

          attributeBreakdownMap.get(attrName)!.push({
            attribute: attrName,
            value: valueResult.attributeValueLabel,
            scores: {
              favorable: Math.round((valueResult.scaleDistribution.favorablePercent || 0) * 100),
              neutral: Math.round((valueResult.scaleDistribution.neutralPercent || 0) * 100),
              unfavorable: Math.round((valueResult.scaleDistribution.unfavorablePercent || 0) * 100),
              average: valueResult.scoreAverage || 0,
              responseCount: valueResult.scaleRespondentCount || 0,
            },
          });
        }
      });
    });

    // Calculate spreads
    attributeScoresMap.forEach((scores, attrName) => {
      const spread = scores.max - scores.min;
      if (spread > 0) {
        question.attributeSpreads.push({
          attribute: attrName,
          spread: Math.round(spread),
          minValue: scores.minValue,
          minScore: scores.min,
          maxValue: scores.maxValue,
          maxScore: scores.max,
          valueCount: attributeBreakdownMap.get(attrName)?.length || 0,
        });
      }
    });

    // Flatten attribute breakdown and sort by favorable score
    attributeBreakdownMap.forEach((breakdowns) => {
      question.attributeBreakdown.push(...breakdowns);
    });
    question.attributeBreakdown.sort((a, b) => a.scores.favorable - b.scores.favorable);

    questions.push(question);
  });

  // Process multiple-choice (option) questions. These are not scored into factors, so they never
  // appear in the base report's surveyElementResults handled above. We build them directly from
  // overallResults, where each option carries its own answer percentage and count.
  overallResults?.forEach((overallResult) => {
    const options = overallResult?.options;
    if (!options || !Array.isArray(options) || options.length === 0) {
      return;
    }

    // Guard against emitting a question already produced by the scale loop above.
    const elementId = overallResult.surveyElementId;
    if (questions.some((existingQuestion) => existingQuestion.id === elementId)) {
      return;
    }

    // Resolve question metadata the same way the scale loop does.
    const allSurveyElements = surveyElements?.[0]?.surveyElements || surveyWithDetails?.surveyElements || [];
    let element = allSurveyElements.find((surveyElement) => surveyElement.id === elementId);

    if (!element && surveyFullDetails?.survey?.questions) {
      const fullDetailQuestion = surveyFullDetails.survey.questions.find((question: any) => question.id === elementId);
      if (fullDetailQuestion) {
        element = {
          id: elementId,
          title: fullDetailQuestion.question?.messagesPerLanguage?.[0]?.message || '',
          categoryId: fullDetailQuestion.question?.category?.id,
          typeName: fullDetailQuestion.type?.name || '',
          typeKey: 'option',
          orderIndex: fullDetailQuestion.orderIndex,
          isRequired: fullDetailQuestion.question?.required || false,
          allowComments: fullDetailQuestion.question?.showTextComment || false,
          translations: {},
        } as any;
      }
    }

    const question: AggregatedSurveyData['questions'][0] = {
      id: elementId,
      title: element?.title || overallResult?.text || '',
      factorId: element?.categoryId || 0,
      factorName: categoryMap.get(element?.categoryId || 0) || `Factor ${element?.categoryId || 0}`,
      type: element?.typeName || element?.typeKey || overallResult?.type || 'Multiple Choice',
      orderIndex: element?.orderIndex || 0,
      isRequired: element?.isRequired || false,
      allowComments: element?.allowComments || false,
      translations:
        element?.translations && Object.keys(element.translations).length > 0
          ? Object.entries(element.translations).reduce(
              (acc, [key, value]: [string, any]) => {
                acc[key] = {
                  ...value,
                  desc: value.desc || '',
                };
                return acc;
              },
              {} as Record<string, any>,
            )
          : undefined,
      // Option questions have no favorable/neutral/unfavorable scale; the distribution below is the signal.
      scores: {
        favorable: 0,
        neutral: 0,
        unfavorable: 0,
        average: overallResult?.average || 0,
      },
      answeredCount: overallResult?.totalAnswered,
      respondentCount: overallResult?.totalRespondents,
      optionDistribution: options.map((option) => ({
        label: option.text,
        // The API returns percentage as a 0-1 fraction; scale to a whole percent.
        percentage: toOneDecimal((option.percentage || 0) * 100),
        count: option.totalAnswered || 0,
      })),
      attributeSpreads: [],
      attributeBreakdown: [],
    };

    questions.push(question);
  });

  // Extract available attributes
  const attributes: AggregatedSurveyData['attributes'] = surveyAttributes.map((attr) => ({
    id: attr.id,
    name: attr.attributeNames?.[0]?.name || `Attribute ${attr.id}`,
    values: attributeResponseStats?.attributes?.find((a) => a.id === attr.id)?.values?.map((v) => v.label) || [],
  }));

  return {
    surveyId: surveyBasicData.id,
    surveyName,
    surveyType,
    respondents,
    responseRate: isOpenSurvey ? null : responseRatePercentage,
    startDate,
    endDate,
    responseRateSpreads,
    responseRatesByAttributeValue,
    factors,
    questions,
    attributes,
  };
}

function formatAggregatedDataAsNarrative(
  data: AggregatedSurveyData,
  includeResponseRatesFor?: number[],
  includeAllResponseRates?: boolean,
): string {
  const narrative: string[] = [];

  // Survey Overview
  narrative.push(`SURVEY OVERVIEW`);
  narrative.push(`Survey Name: ${data.surveyName}`);
  narrative.push(`Survey Type: ${data.surveyType}`);
  narrative.push(`Survey ID: ${data.surveyId}`);
  narrative.push(`Total Respondents: ${data.respondents}`);
  narrative.push(
    data.responseRate === null
      ? 'Response Rate: none. This is an Open Survey: anyone with the link can respond and nobody was invited, so there is no population to measure a response rate against. Do not estimate or mention one.'
      : `Response Rate: ${data.responseRate}%`,
  );
  narrative.push(`Survey Period: ${data.startDate} to ${data.endDate}`);
  narrative.push('');

  // Factor Analysis
  narrative.push(`FACTOR ANALYSIS (${data.factors.length} factors)`);
  narrative.push('');

  data.factors.forEach((factor) => {
    narrative.push(`Factor: ${factor.name}`);

    if (factor.isEnps && factor.enpsScores) {
      narrative.push(`  eNPS score: ${factor.enpsScores.score} (promoters % minus detractors %, ranges -100 to 100)`);
      narrative.push(`  eNPS distribution:`);
      narrative.push(`    Promoters (rated 9-10): ${factor.enpsScores.promoters}%`);
      narrative.push(`    Passives (rated 7-8): ${factor.enpsScores.passives}%`);
      narrative.push(`    Detractors (rated 6 or below): ${factor.enpsScores.detractors}%`);
    } else {
      narrative.push(`  Favorable: ${factor.scores.favorable}%`);
      narrative.push(`  Neutral: ${factor.scores.neutral}%`);
      narrative.push(`  Unfavorable: ${factor.scores.unfavorable}%`);
    }

    if (factor.variability) {
      narrative.push(`  Variability Analysis:`);
      narrative.push(`    Standard Deviation: ${factor.variability.standardDeviation}`);
      narrative.push(`    Coefficient of Variation: ${factor.variability.coefficientOfVariation}`);
      narrative.push(`    Range: ${factor.variability.range} (${factor.variability.min} - ${factor.variability.max})`);
      narrative.push(`    Sample Size: ${factor.variability.sampleSize} groups`);
    }

    if (factor.attributeSpreads && factor.attributeSpreads.length > 0) {
      narrative.push(`  Attribute Spreads:`);
      factor.attributeSpreads.forEach((spread) => {
        narrative.push(`    ${spread.attribute}: ${spread.spread}% spread`);
        narrative.push(`      Lowest: ${spread.minValue} (${spread.minScore}%)`);
        narrative.push(`      Highest: ${spread.maxValue} (${spread.maxScore}%)`);
      });
    }

    if (factor.attributeBreakdown.length > 0) {
      narrative.push(`  Breakdown by Attributes (sorted by lowest to highest favorable):`);
      factor.attributeBreakdown.forEach((breakdown) => {
        narrative.push(`    ${breakdown.attribute}: ${breakdown.value}`);
        narrative.push(
          `      Favorable: ${breakdown.scores.favorable}%, Neutral: ${breakdown.scores.neutral}%, Unfavorable: ${breakdown.scores.unfavorable}%`,
        );
        narrative.push(`      Responses: ${breakdown.scores.responseCount}`);
      });
    }
    narrative.push('');
  });

  // Question Analysis
  narrative.push(`QUESTION ANALYSIS (${data.questions.length} questions)`);
  narrative.push('');

  // Group questions by factor
  const questionsByFactor = data.questions.reduce<Record<string, typeof data.questions>>((acc, question) => {
    const factorName = coalesce(question.factorName, 'Uncategorized');
    if (!acc[factorName]) {
      acc[factorName] = [];
    }
    acc[factorName].push(question);
    return acc;
  }, {});

  Object.entries(questionsByFactor).forEach(([factorName, questions]) => {
    narrative.push(`Questions in ${factorName}:`);
    narrative.push('');

    questions.forEach((question) => {
      narrative.push(`  Question: "${question.title}"`);
      narrative.push(`  Type: ${question.type}`);
      narrative.push(`  Order: ${question.orderIndex}`);
      narrative.push(`  Required: ${question.isRequired ? 'Yes' : 'No'}`);
      narrative.push(`  Allow Comments: ${question.allowComments ? 'Yes' : 'No'}`);

      if (question.optionDistribution && question.optionDistribution.length > 0) {
        // Multiple-choice question: report the per-option answer distribution.
        narrative.push(`  Answer Distribution (multiple choice):`);
        question.optionDistribution.forEach((option) => {
          narrative.push(`    "${option.label}": ${option.percentage}% (${option.count} responses)`);
        });
      } else if (question.enps) {
        // eNPS question: it is scored as eNPS rather than favourability, so it has no favourable,
        // neutral or unfavourable share to report.
        narrative.push(`  eNPS score: ${question.enps.score} (promoters % minus detractors %, ranges -100 to 100)`);
        narrative.push(
          `  eNPS distribution: Promoters (rated 9-10) ${question.enps.promoters}%, Passives (rated 7-8) ${question.enps.passives}%, Detractors (rated 6 or below) ${question.enps.detractors}%`,
        );
      } else {
        narrative.push(
          `  Scores: Favorable ${question.scores.favorable}%, Neutral ${question.scores.neutral}%, Unfavorable ${question.scores.unfavorable}%`,
        );
      }

      if (typeof question.answeredCount === 'number' && typeof question.respondentCount === 'number') {
        narrative.push(`  Answered by: ${question.answeredCount} of ${question.respondentCount} respondents`);
      }

      if (question.ratingScale) {
        narrative.push(`  Rating scale: ${question.ratingScale.min} to ${question.ratingScale.max}`);
      }

      if (question.ratingDistribution && question.ratingDistribution.length > 0) {
        const ratings = question.ratingDistribution
          .map((rating) => `${rating.rating}: ${rating.count} responses (${rating.percentage}%)`)
          .join(', ');
        narrative.push(`  Rating Distribution (exact response counts): ${ratings}`);
      }

      if (question.variability) {
        narrative.push(`  Variability Analysis:`);
        narrative.push(`    Standard Deviation: ${question.variability.standardDeviation}`);
        narrative.push(`    Coefficient of Variation: ${question.variability.coefficientOfVariation}`);
        narrative.push(
          `    Range: ${question.variability.range} (${question.variability.min} - ${question.variability.max})`,
        );
        narrative.push(`    Sample Size: ${question.variability.sampleSize} groups`);
      }

      if (question.attributeSpreads && question.attributeSpreads.length > 0) {
        narrative.push(`  Attribute Spreads:`);
        question.attributeSpreads.forEach((spread) => {
          narrative.push(`    ${spread.attribute}: ${spread.spread}% spread`);
          narrative.push(`      Lowest: ${spread.minValue} (${spread.minScore}%)`);
          narrative.push(`      Highest: ${spread.maxValue} (${spread.maxScore}%)`);
        });
      }

      if (question.attributeBreakdown.length > 0) {
        narrative.push(`  Breakdown by Attributes (sorted by lowest to highest favorable):`);
        question.attributeBreakdown.forEach((breakdown) => {
          narrative.push(`    ${breakdown.attribute}: ${breakdown.value}`);
          narrative.push(
            `      Favorable: ${breakdown.scores.favorable}%, Neutral: ${breakdown.scores.neutral}%, Unfavorable: ${breakdown.scores.unfavorable}%`,
          );
          narrative.push(`      Responses: ${breakdown.scores.responseCount}`);
        });
      }
      narrative.push('');
    });
  });

  // Response Rate Analysis
  if (data.responseRateSpreads.length > 0) {
    narrative.push(`RESPONSE RATE SPREADS BY ATTRIBUTE`);
    narrative.push('');

    data.responseRateSpreads.forEach((spread) => {
      narrative.push(`${spread.attribute}: ${spread.spread}% spread across ${spread.valueCount} values`);
      narrative.push(`  Lowest: ${spread.minValue} (${spread.minResponseRate}%)`);
      narrative.push(`  Highest: ${spread.maxValue} (${spread.maxResponseRate}%)`);
      narrative.push('');
    });
  }

  // The exhaustive per-value response rate breakdown is the single largest contributor to this
  // tool's output. It is omitted by default (the spreads section above already answers "where are
  // the gaps?") and only rendered for the attribute IDs explicitly requested via includeResponseRatesFor.
  // When the `coach-receives-response-rates` flag is on, the legacy behaviour is preserved and every
  // attribute's breakdown is rendered.
  const includeResponseRatesSet = new Set(includeResponseRatesFor || []);
  const responseRatesToRender = includeAllResponseRates
    ? data.responseRatesByAttributeValue
    : data.responseRatesByAttributeValue.filter((rate) => includeResponseRatesSet.has(rate.attributeId));

  if (responseRatesToRender.length > 0) {
    narrative.push(`RESPONSE RATES BY ATTRIBUTE VALUE`);
    narrative.push('');

    let currentAttribute = '';
    responseRatesToRender.forEach((rate) => {
      if (rate.attribute !== currentAttribute) {
        if (currentAttribute !== '') {
          narrative.push('');
        }
        currentAttribute = rate.attribute;
        narrative.push(`${rate.attribute}:`);
      }
      narrative.push(`  ${rate.value}: ${rate.responseRate}% (${rate.responseCount}/${rate.totalCount} responses)`);
    });
    narrative.push('');
  }

  // Attributes Summary
  narrative.push(`AVAILABLE ATTRIBUTES (${data.attributes.length} attributes)`);
  data.attributes.forEach((attr) => {
    narrative.push(`${attr.name}: ${attr.values.length} values`);
    if (attr.values.length > 0) {
      narrative.push(`  - ${attr.values.join(', ')}`);
    }
  });

  return narrative.join('\n');
}
/* istanbul ignore stop */

// Default export for backward compatibility
export const getSurveyAggregatedResultsTool = createSurveyAggregatedResultsTool({ peopleLeaderAttributeId: null });
